import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FilterGroup } from './AdvancedFilterBuilder';

interface SaveCohortModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => Promise<void>;
  filters: FilterGroup;
  userCount: number;
  applicationId: string;
}

const SaveCohortModal: React.FC<SaveCohortModalProps> = ({
  isOpen,
  onClose,
  onSave,
  filters,
  userCount,
  applicationId,
}) => {
  const { t } = useTranslation('retention');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(name, description);
      // Reset form on success
      setName('');
      setDescription('');
      onClose();
    } catch (err: any) {
      setError(err.message || t('saveModal.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('saveModal.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('saveModal.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Name input */}
            <div>
              <label htmlFor="cohort-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('saveModal.name')}
              </label>
              <input
                type="text"
                id="cohort-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('saveModal.namePlaceholder')}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            {/* Description input */}
            <div>
              <label htmlFor="cohort-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('saveModal.description')}
              </label>
              <textarea
                id="cohort-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('saveModal.descriptionPlaceholder')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            {/* Preview */}
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                {t('saveModal.preview', { count: userCount })}
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-4 py-3">
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {t('saveModal.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSaving ? t('saveModal.saving') : t('saveModal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveCohortModal;
